import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ChatBubbleBottomCenterTextIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { NODE_TYPE } from "../../services/Constants";
import { capitaliseWords } from "../../services/Utils";
import { Button } from "../Button";
import { TextField } from "../form-fields/TextField";
import { APIs } from "../../services/Apis";
import { toast } from "react-toastify";
import { RedactorEditor } from "../RedactorEditor";
import { MultiStepLoaderNodeSettings } from "../loaders/MultiStepLoaderNodeSettings";
import { SELECTED_TYPE } from "./GoalWorkflowPanel";
import { SqlEditor } from "../form-fields/SqlEditor";

export const NodeSettingsPanel = ({
  isOpen,
  onClose,
  selectedType,
  selectedAction,
  segment,
  onRefresh,
  isOnboarding = false,
}) => {
  // Goal details state
  const [goalTitle, setGoalTitle] = useState("");
  const [goalQuery, setGoalQuery] = useState("");
  const [goalHumanExplanation, setGoalHumanExplanation] = useState("");
  const [goalOriginalQuery, setGoalOriginalQuery] = useState("");
  const [goalOriginalHumanExplanation, setGoalOriginalHumanExplanation] =
    useState("");
  const [goalLoading, setGoalLoading] = useState(false);
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalActiveTab, setGoalActiveTab] = useState("explanation");

  // SQL validation state
  const [goalQueryHasErrors, setGoalQueryHasErrors] = useState(false);

  // Approval state
  const [approvingSaving, setApprovingSaving] = useState(false);

  // Action details state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSaving, setActionSaving] = useState(false);
  const [htmlEmailContent, setHtmlEmailContent] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [actionFormData, setActionFormData] = useState({});
  const htmlEmailContentRef = useRef("");

  // Reset when panel opens or selection changes
  useEffect(() => {
    if (!isOpen) return;
    setHtmlEmailContent("");
    setEmailSubject("");
    htmlEmailContentRef.current = "";
  }, [isOpen, selectedType, selectedAction?.id]);

  // Fetch goal details
  useEffect(() => {
    if (!isOpen || !segment || selectedType !== SELECTED_TYPE.GOAL) return;
    if (isOnboarding) return;

    setGoalTitle(
      segment.outcomeGoal || segment.title || segment.description || "",
    );
    setGoalQuery(segment.query || "");
    setGoalOriginalQuery(segment.query || "");
    setGoalHumanExplanation(segment.humanExplanationQuery || "");
    setGoalOriginalHumanExplanation(segment.humanExplanationQuery || "");
    setGoalLoading(false);
  }, [isOpen, segment, selectedType, isOnboarding]);

  // Fetch action details
  useEffect(() => {
    if (!isOpen || !selectedAction || selectedType !== SELECTED_TYPE.ACTION)
      return;
    if (isOnboarding) return;

    if (
      selectedAction.type === NODE_TYPE.EMAIL ||
      selectedAction.emailContent
    ) {
      setHtmlEmailContent(
        selectedAction.emailContent || selectedAction.htmlEmailContent || "",
      );
      htmlEmailContentRef.current =
        selectedAction.emailContent || selectedAction.htmlEmailContent || "";
      setEmailSubject(selectedAction.emailSubject || "");
    }
    setActionFormData({
      ...selectedAction,
    });
    setActionLoading(false);
  }, [isOpen, selectedAction, selectedType, isOnboarding]);

  const handleEmailContentChange = useCallback((html) => {
    setHtmlEmailContent(html);
    htmlEmailContentRef.current = html;
  }, []);

  const handleActionInputChange = useCallback((field, value) => {
    setActionFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Goal save handlers
  const handleSaveGoalExplanation = async () => {
    if (!segment?.id && !segment?.segmentId) return;
    if (isOnboarding) return;
    const targetId = segment.id || segment.segmentId;
    setGoalSaving(true);
    try {
      const response = await APIs.editSegment(
        targetId,
        null,
        goalHumanExplanation,
      );
      if (response && response.success) {
        if (response.query) {
          setGoalQuery(response.query);
          setGoalOriginalQuery(response.query);
        }
        setGoalOriginalHumanExplanation(goalHumanExplanation);
        toast.success("Explanation saved and query generated!");
        if (onRefresh) onRefresh();
        if (onClose) onClose();
      } else {
        toast.error(response.message || "Failed to save explanation");
      }
    } catch (error) {
      console.error("Error saving explanation:", error);
    } finally {
      setGoalSaving(false);
    }
  };

  const handleSaveGoalQuery = async () => {
    if (!segment?.id && !segment?.segmentId) return;
    if (isOnboarding) return;
    if (goalQueryHasErrors) {
      toast.error("Please fix the SQL errors before saving");
      return;
    }
    const targetId = segment.id || segment.segmentId;
    setGoalSaving(true);
    try {
      const response = await APIs.editSegment(targetId, goalQuery, null);
      if (response && response.success) {
        if (response.humanExplanationQuery) {
          setGoalHumanExplanation(response.humanExplanationQuery);
          setGoalOriginalHumanExplanation(response.humanExplanationQuery);
        }
        setGoalOriginalQuery(goalQuery);
        toast.success("Query saved and explanation generated!");
        if (onRefresh) onRefresh();
        if (onClose) onClose();
      } else {
        toast.error(response.message || "Failed to save query");
      }
    } catch (error) {
      console.error("Error saving query:", error);
    } finally {
      setGoalSaving(false);
    }
  };

  // Action save handler
  const handleSaveAction = async () => {
    if (!selectedAction?.id && !selectedAction?.segmentActionId) return;
    const targetId = selectedAction.id || selectedAction.segmentActionId;
    setActionSaving(true);
    try {
      let response;
      if (
        selectedAction.type === NODE_TYPE.EMAIL ||
        selectedAction.emailContent
      ) {
        const latestContent = htmlEmailContentRef.current || htmlEmailContent;
        response = await APIs.editSegmentAction(
          targetId,
          emailSubject,
          typeof latestContent === "string" ? latestContent : "",
        );
      } else if (selectedAction.type === NODE_TYPE.DELAY) {
        response = await APIs.editSegmentAction(
          targetId,
          null,
          null,
          actionFormData.delay || actionFormData.duration || 0,
          actionFormData.delayUnit || actionFormData.durationUnit || "hours",
        );
      }
      if (response?.success) {
        toast.success("Saved successfully!");
        if (onRefresh) onRefresh();
        if (onClose) onClose();
      } else {
        toast.error(response?.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error saving action:", error);
    } finally {
      setActionSaving(false);
    }
  };

  // Approval handler
  const handleApprove = async () => {
    setApprovingSaving(true);
    try {
      let response;
      if (selectedType === SELECTED_TYPE.ACTION) {
        const targetId = selectedAction?.id || selectedAction?.segmentActionId;
        if (!targetId) return;
        response = await APIs.approveSegmentAction(targetId, true);
      } else {
        const targetId = segment?.id || segment?.segmentId;
        if (!targetId) return;
        response = await APIs.approveSegment(targetId, true);
      }
      if (response && response.success) {
        toast.success(
          selectedType === SELECTED_TYPE.ACTION
            ? "Action approved!"
            : "Goal approved!",
        );
        if (onRefresh) onRefresh();
        if (onClose) onClose();
      } else {
        toast.error(response?.message || "Failed to approve");
      }
    } catch (error) {
      console.error("Error approving:", error);
    } finally {
      setApprovingSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter a recipient email address");
      return;
    }
    const subject = emailSubject || "(No subject)";
    const html = htmlEmailContentRef.current || htmlEmailContent;
    if (!html) {
      toast.error("Please add some email content before sending a test");
      return;
    }
    setSendingTest(true);
    try {
      const response = await APIs.sendTestEmail(testEmail, subject, html);
      if (response.success) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.error(response.message || "Failed to send test email");
      }
    } catch (error) {
      console.error("Error sending test email:", error);
    } finally {
      setSendingTest(false);
    }
  };

  const hasUnsavedGoalExplanation =
    goalHumanExplanation !== goalOriginalHumanExplanation;
  const hasUnsavedGoalQuery = goalQuery !== goalOriginalQuery;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/25 transition-opacity duration-300 ease-in-out data-closed:opacity-0"
      />
      <div className="fixed inset-0 overflow-hidden ">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-4xl transform transition duration-300 ease-in-out data-closed:translate-x-full"
            >
              <div className="flex flex-col h-full bg-white shadow-2xl overflow-hidden border-l border-gray-200">
                <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    {selectedType === SELECTED_TYPE.GOAL && (
                      <>
                        <SparklesIcon className="w-5 h-5 text-gray-900" />
                        Goal Configuration
                      </>
                    )}
                    {selectedType === SELECTED_TYPE.ACTION &&
                      selectedAction && (
                        <>
                          {selectedAction.type === NODE_TYPE.EMAIL ||
                          selectedAction.emailContent !== undefined ? (
                            <PaperAirplaneIcon className="w-5 h-5 text-gray-900" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5 text-gray-900" />
                          )}
                          {capitaliseWords(
                            selectedAction.emailContent !== undefined
                              ? "email"
                              : selectedAction.type || "Action",
                          )}{" "}
                          Configuration
                        </>
                      )}
                  </h2>

                  <div className="flex items-center gap-3">
                    {selectedType === SELECTED_TYPE.ACTION &&
                      !actionLoading && (
                        <>
                          <Button
                            variant="solid"
                            color="primary"
                            size="sm"
                            onClick={handleSaveAction}
                            disabled={actionSaving}
                            loading={actionSaving}
                          >
                            {actionSaving ? "Saving..." : "Save Changes"}
                          </Button>
                          {selectedAction?.approved === false && (
                            <Button
                              variant="solid"
                              color="slate"
                              size="sm"
                              onClick={handleApprove}
                              disabled={approvingSaving}
                              loading={approvingSaving}
                            >
                              {approvingSaving ? "Approving..." : "Approve"}
                            </Button>
                          )}
                        </>
                      )}
                    {selectedType === SELECTED_TYPE.GOAL &&
                      !goalLoading &&
                      segment?.ready !== false && (
                        <>
                          <Button
                            variant="solid"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              if (goalActiveTab === "explanation") {
                                handleSaveGoalExplanation();
                              } else {
                                handleSaveGoalQuery();
                              }
                            }}
                            disabled={
                              goalSaving ||
                              (!hasUnsavedGoalExplanation &&
                                !hasUnsavedGoalQuery) ||
                              (goalActiveTab === "query" && goalQueryHasErrors)
                            }
                            loading={goalSaving}
                          >
                            {goalSaving ? "Saving..." : "Save Changes"}
                          </Button>
                          {segment?.approved === false && (
                            <Button
                              variant="solid"
                              color="slate"
                              size="sm"
                              onClick={handleApprove}
                              disabled={approvingSaving}
                              loading={approvingSaving}
                            >
                              {approvingSaving ? "Approving..." : "Approve"}
                            </Button>
                          )}
                        </>
                      )}
                    <Button variant="outline" size="sm" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 flex flex-col">
                  {selectedType === SELECTED_TYPE.GOAL && (
                    <GoalContent
                      loading={goalLoading}
                      saving={goalSaving}
                      title={goalTitle}
                      humanExplanation={goalHumanExplanation}
                      query={goalQuery}
                      activeTab={goalActiveTab}
                      onTabChange={setGoalActiveTab}
                      onHumanExplanationChange={(e) =>
                        setGoalHumanExplanation(e.target.value)
                      }
                      onQueryChange={setGoalQuery}
                      onQueryValidationChange={setGoalQueryHasErrors}
                      isOnboarding={isOnboarding}
                      notReady={segment?.ready === false}
                    />
                  )}

                  {selectedType === SELECTED_TYPE.ACTION && selectedAction && (
                    <ActionContent
                      action={selectedAction}
                      loading={actionLoading}
                      formData={actionFormData}
                      htmlEmailContent={htmlEmailContent}
                      emailSubject={emailSubject}
                      testEmail={testEmail}
                      sendingTest={sendingTest}
                      onEmailSubjectChange={setEmailSubject}
                      onEmailContentChange={handleEmailContentChange}
                      onTestEmailChange={setTestEmail}
                      onSendTestEmail={handleSendTestEmail}
                      onInputChange={handleActionInputChange}
                      actionId={
                        selectedAction.segmentActionId || selectedAction.id
                      }
                    />
                  )}
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

function GoalContentGenerating() {
  return (
    <div className="space-y-6">
      <div className="animate-pulse space-y-2">
        <div className="h-4 w-16 bg-gray-200 rounded" />
        <div className="h-10 w-full bg-gray-100 rounded-lg" />
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative w-5 h-5 shrink-0">
            <div className="absolute inset-0 rounded-full border-2 border-blue-200" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-500 animate-spin" />
          </div>
          <span className="text-sm font-semibold text-blue-800">
            Generating your goal...
          </span>
        </div>
        <div className="animate-pulse space-y-3 pl-8">
          <div className="h-3 w-full bg-blue-100 rounded" />
          <div className="h-3 w-5/6 bg-blue-100 rounded" />
          <div className="h-3 w-4/6 bg-blue-100 rounded" />
          <div className="h-10 w-full bg-blue-100/70 rounded-lg mt-4" />
          <div className="h-3 w-3/4 bg-blue-100 rounded" />
          <div className="h-3 w-2/3 bg-blue-100 rounded" />
        </div>
        <p className="text-xs text-blue-500 mt-4 pl-8">
          This may take a moment. The goal description and database query are
          being prepared.
        </p>
      </div>
    </div>
  );
}

function GoalContent({
  loading,
  title,
  humanExplanation,
  query,
  activeTab,
  onTabChange,
  onHumanExplanationChange,
  onQueryChange,
  onQueryValidationChange,
  isOnboarding,
  notReady,
}) {
  const explanationRef = useRef(null);

  const autoResize = useCallback((ref) => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  }, []);

  useEffect(() => {
    autoResize(explanationRef);
  }, [humanExplanation, activeTab, autoResize]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="relative w-8 h-8">
          <div className="absolute inset-0 rounded-full border-4 border-neutral-200" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (notReady) {
    return <GoalContentGenerating />;
  }

  return (
    <div className="space-y-6">
      <TextField
        label="Title"
        type="text"
        value={title}
        placeholder="Goal title"
        readOnly
      />

      {/* Tabs */}
      <div>
        <div className="bg-gray-100/70 p-1 rounded-full inline-flex mb-2">
          <button
            onClick={() => onTabChange("explanation")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer ${
              activeTab === "explanation"
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-black/5"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
            }`}
          >
            <ChatBubbleBottomCenterTextIcon className="w-4 h-4" />
            Goal Description
          </button>
          <button
            onClick={() => onTabChange("query")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer ${
              activeTab === "query"
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-black/5"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
            }`}
          >
            <CodeBracketIcon className="w-4 h-4" />
            Database Query (Advanced)
          </button>
        </div>

        <div className="mt-4">
          {activeTab === "explanation" && (
            <div className="flex flex-col gap-3">
              <textarea
                ref={explanationRef}
                value={humanExplanation}
                onChange={(e) => {
                  onHumanExplanationChange(e);
                  autoResize(explanationRef);
                }}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px] shadow-sm resize-none overflow-hidden"
                placeholder="Describe who should be in this segment (e.g., 'Active users who haven't purchased in 60 days')"
                disabled={isOnboarding}
              />
            </div>
          )}

          {activeTab === "query" && (
            <div className="flex flex-col gap-3">
              <SqlEditor
                value={query}
                onChange={onQueryChange}
                disabled={isOnboarding}
                onValidationChange={onQueryValidationChange}
                placeholder="Query will be generated automatically..."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TestEmailAccordion({
  testEmail,
  sendingTest,
  onTestEmailChange,
  onSendTestEmail,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          <PaperAirplaneIcon className="w-4 h-4 text-gray-900" />
          <span className="text-sm font-semibold text-gray-800">
            Send Test Email
          </span>
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>
      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <p className="text-sm text-gray-500 mt-3 mb-4">
            Send a preview of this email to verify it looks correct.
          </p>
          <div className="flex items-center gap-3">
            <TextField
              type="email"
              value={testEmail}
              onChange={(e) => onTestEmailChange(e.target.value)}
              className="flex-1"
              placeholder="recipient@example.com"
            />
            <Button
              variant="outline"
              onClick={onSendTestEmail}
              disabled={sendingTest || !testEmail}
              loading={sendingTest}
              className="bg-white"
            >
              {sendingTest ? "Sending..." : "Send Test"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionContent({
  action,
  loading,
  formData,
  htmlEmailContent,
  emailSubject,
  testEmail,
  sendingTest,
  onEmailSubjectChange,
  onEmailContentChange,
  onTestEmailChange,
  onSendTestEmail,
  onInputChange,
  actionId,
}) {
  if (loading) {
    return (
      <MultiStepLoaderNodeSettings loading={loading} actionId={actionId} />
    );
  }

  return (
    <div className="space-y-8">
      {action.type === NODE_TYPE.EMAIL || action.emailContent !== undefined ? (
        <div>
          <div className="space-y-5">
            <TextField
              label="Subject"
              type="text"
              value={emailSubject}
              onChange={(e) => onEmailSubjectChange(e.target.value)}
              placeholder="Email subject"
            />
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Email Content
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <RedactorEditor
                  value={htmlEmailContent}
                  onChange={onEmailContentChange}
                  placeholder="Enter email content..."
                />
              </div>
            </div>

            <TestEmailAccordion
              testEmail={testEmail}
              sendingTest={sendingTest}
              onTestEmailChange={onTestEmailChange}
              onSendTestEmail={onSendTestEmail}
            />
          </div>
        </div>
      ) : null}

      {action.type === NODE_TYPE.DELAY || action.type === "delay" ? (
        <div>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Duration
              </label>
              <div className="flex rounded-lg shadow-sm border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <input
                  type="number"
                  value={formData.delay || formData.duration || ""}
                  onChange={(e) => onInputChange("delay", e.target.value)}
                  className="flex-1 px-4 py-2.5 bg-white focus:outline-none min-w-0"
                  placeholder="24"
                />
                <div className="w-px bg-gray-300" />
                <select
                  value={formData.delayUnit || formData.durationUnit || "hours"}
                  onChange={(e) => onInputChange("delayUnit", e.target.value)}
                  className="px-4 py-2.5 bg-gray-50 border-0 focus:outline-none text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {action.type === NODE_TYPE.FILTER || action.type === "filter" ? (
        <div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Filter Settings
          </h3>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                Condition
              </label>
              <select
                value={formData.condition || ""}
                onChange={(e) => onInputChange("condition", e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Select condition</option>
                <option value="opened">Email Opened</option>
                <option value="clicked">Link Clicked</option>
                <option value="not_opened">Not Opened</option>
              </select>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
